/**
 * Sessão do usuário no cliente.
 *
 * IMPORTANTE: `can()` serve para ESCONDER ações que o usuário não pode executar.
 * Não é controle de acesso — o servidor revalida toda operação (§4 da
 * especificação). Ocultar um botão melhora a experiência; não protege nada.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CurrentUser, SituacaoLegalDaSessao } from '@crm/contracts';
import { api, registrarAvisoDeAceitePendente, registrarAvisoDeCapacidadeAlterada } from './api';

/** CERES-ADMIN-PLANOS-001: intervalo mínimo entre reconsultas automáticas da sessão. */
export const INTERVALO_MINIMO_DE_RECONSULTA_MS = 60_000;
import { queryClient } from './queryClient';
import { definirFormatacao } from './format';
import { definirIdioma } from './i18n';

/**
 * O que a sessão assume enquanto não há resposta do servidor.
 *
 * `tudoAceito: true` — otimista de propósito. O gate é uma PARADA: assumir
 * pendência antes de saber levaria toda sessão carregando a bater na tela de
 * aceite por uma fração de segundo, inclusive a de quem já aceitou tudo. O
 * bloqueio de verdade está no servidor, que não depende deste palpite.
 */
const LEGAL_DESCONHECIDO: SituacaoLegalDaSessao = { tudoAceito: true, pendentes: [] };

interface SessionState {
  user: CurrentUser | null;
  /** Situação dos aceites contratuais desta sessão (§21.10). */
  legal: SituacaoLegalDaSessao;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Troca a organização ativa da sessão (CERES-008). Limpa TODO o cache de
   * consultas: dado da organização anterior em tela depois da troca seria
   * vazamento visual de tenant.
   */
  trocarOrganizacao: (organizationId: string) => Promise<void>;
  /**
   * CERES-ADM-002: leva a sessão à Administração da plataforma. Explícito —
   * nenhuma sessão vai para lá sozinha — e limpa o cache, como a troca de
   * organização: nada do tenant anterior pode continuar na tela.
   */
  entrarNaPlataforma: () => Promise<void>;
  /**
   * CERES-ADM-004-CORRECAO-02: volta à escolha de contexto (sai da plataforma
   * ou da organização escolhida) sem encerrar a sessão.
   */
  voltarAEscolha: () => Promise<void>;
  /** `true` se o usuário possui a permissão. Apenas para a interface. */
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  /** Conveniência visual; o backend revalida o mesmo gate. */
  hasFeature: (feature: string) => boolean;
  /**
   * `true` só com a sessão RESOLVIDA: carregamento concluído, usuário com
   * organização válida e lista de capacidades recebida (0.131.0). Destinos
   * sensíveis (Comissões) exigem isto além de permissão e capacidade — em
   * carregamento, erro ou resposta malformada, ficam ocultos.
   */
  sessaoPronta: boolean;
}

/**
 * A sessão está RESOLVIDA? Fail-closed: qualquer dúvida responde `false`.
 * Exportada para teste — o provedor segura a tela em carregamento, então o
 * estado intermediário não é alcançável montando a aplicação inteira.
 */
export function sessaoEstaPronta(loading: boolean, user: CurrentUser | null | undefined): boolean {
  return (
    !loading &&
    user !== null &&
    user !== undefined &&
    typeof user.organizationId === 'string' &&
    user.organizationId !== '' &&
    Array.isArray(user.productFeatures)
  );
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * O idioma e o fuso da pessoa entram em vigor JUNTO com a sessão (§20.5).
 *
 * Aqui, e não em cada tela: as funções de formatação são chamadas de dezenas de
 * lugares, inclusive de código que não é componente. Aplicar no ponto em que a
 * sessão chega evita o pior desfecho — a data aparecer em português e se
 * reescrever meio segundo depois, que faz a interface parecer instável.
 *
 * Sem sessão, volta ao padrão: a tela de login não herda a preferência de quem
 * saiu.
 */
function aplicarIdiomaEFuso(user: CurrentUser | null): void {
  const idioma = user?.idioma ?? 'pt-BR';
  const fuso = user?.fuso ?? 'America/Sao_Paulo';
  definirFormatacao({ idioma, fuso });
  definirIdioma(idioma);
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [legal, setLegal] = useState<SituacaoLegalDaSessao>(LEGAL_DESCONHECIDO);
  const [loading, setLoading] = useState(true);

  /**
   * Contador de escritas na sessão, para descartar resposta ATRASADA.
   *
   * DEFEITO QUE ISTO CORRIGE, encontrado ao automatizar o teste de formulário:
   * ao abrir a aplicação, o provedor consulta `/auth/me` e recebe 401 — resposta
   * correta para quem ainda não entrou. Se a pessoa digitar e clicar em "Entrar"
   * ANTES de esse 401 chegar (autopreenchimento do navegador, gerenciador de
   * senhas, ou simplesmente digitação rápida), o login conclui, grava a sessão, e
   * então o 401 antigo chega e apaga tudo. A pessoa é devolvida à tela de login
   * sem explicação nenhuma — parece "o login não funciona às vezes", que é o tipo
   * de defeito que ninguém consegue reproduzir sob demanda.
   *
   * A correção é ignorar o resultado de qualquer consulta iniciada ANTES da
   * última escrita: quem chegou depois manda. Um `AbortController` resolveria o
   * caso da requisição em voo, mas não o da resposta já recebida e ainda não
   * processada — o contador cobre os dois.
   */
  const geracao = useRef(0);

  const aplicar = useCallback(
    (
      minhaGeracao: number,
      proximo: CurrentUser | null,
      situacao: SituacaoLegalDaSessao = LEGAL_DESCONHECIDO,
    ): void => {
      if (minhaGeracao < geracao.current) return;
      geracao.current = minhaGeracao;
      setUser(proximo);
      aplicarIdiomaEFuso(proximo);
      // Anda junto do usuário pelo mesmo contador de geração: se a situação legal
      // fosse gravada por fora, uma resposta atrasada poderia parear o estado
      // legal de uma sessão com o usuário de outra.
      setLegal(situacao);
    },
    [],
  );

  const refresh = useCallback(async (): Promise<void> => {
    const minhaGeracao = geracao.current + 1;
    try {
      const body = await api.get<{ user: CurrentUser; legal?: SituacaoLegalDaSessao }>('/auth/me');
      aplicar(minhaGeracao, body.user, body.legal ?? LEGAL_DESCONHECIDO);
    } catch {
      // 401 é o caso NORMAL de "não autenticado"; qualquer outra falha também
      // deixa a aplicação sem sessão utilizável, então o efeito é o mesmo.
      aplicar(minhaGeracao, null);
    } finally {
      setLoading(false);
    }
  }, [aplicar]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Quando o servidor passa a exigir aceite, a sessão se reconsulta.
   *
   * Uma versão nova dos Termos entra em vigor com gente trabalhando: a partir
   * dali toda requisição é recusada, e a tela montada continuaria enchendo de
   * erro. Aqui a pendência vira estado, e o desvio para `/aceite` passa a ser o
   * mesmo de qualquer outro carregamento.
   */
  useEffect(() => {
    registrarAvisoDeAceitePendente(() => {
      void refresh();
    });
    return () => registrarAvisoDeAceitePendente(null);
  }, [refresh]);

  /**
   * CERES-ADMIN-PLANOS-001 — os direitos na tela acompanham o servidor.
   *
   * Reconsulta `/auth/me` quando o servidor nega uma capacidade
   * (FEATURE_DISABLED) e quando a pessoa volta à aba — no máximo uma vez a cada
   * 60 s, para que uma tela cheia de consultas negadas não vire uma rajada de
   * `/auth/me`. O servidor continua sendo a autoridade: isto só tira da tela o
   * que ele já nega (ou mostra o que ele passou a conceder).
   */
  const ultimaReconsulta = useRef(0);
  useEffect(() => {
    const reconsultar = () => {
      const agora = Date.now();
      if (agora - ultimaReconsulta.current < INTERVALO_MINIMO_DE_RECONSULTA_MS) return;
      ultimaReconsulta.current = agora;
      void refresh();
    };
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') reconsultar();
    };
    registrarAvisoDeCapacidadeAlterada(reconsultar);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      registrarAvisoDeCapacidadeAlterada(null);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const body = await api.post<{ user: CurrentUser; legal?: SituacaoLegalDaSessao }>(
      '/auth/login',
      { email, password },
    );
    // Incrementa a geração: o 401 da consulta inicial, se ainda estiver em voo,
    // passa a ser antigo e não apaga esta sessão.
    geracao.current += 1;
    setUser(body.user);
    aplicarIdiomaEFuso(body.user);
    setLegal(body.legal ?? LEGAL_DESCONHECIDO);
    setLoading(false);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Também avança a geração: uma consulta em voo que ainda devolveria o
      // usuário não deve ressuscitar a sessão encerrada.
      geracao.current += 1;
      setUser(null);
      setLegal(LEGAL_DESCONHECIDO);
      // Nada da sessão encerrada sobrevive no cache: a próxima pessoa neste
      // navegador não pode encontrar o pipeline de quem saiu.
      queryClient.clear();
    }
  }, []);

  const trocarOrganizacao = useCallback(async (organizationId: string): Promise<void> => {
    const body = await api.post<{ user: CurrentUser; legal?: SituacaoLegalDaSessao }>(
      '/auth/organizacao',
      { organizationId },
    );
    // Mesma disciplina do login: a resposta da troca é a mais nova que existe.
    geracao.current += 1;
    setUser(body.user);
    aplicarIdiomaEFuso(body.user);
    setLegal(body.legal ?? LEGAL_DESCONHECIDO);
    // O contexto trocou INTEIRO: dashboard, empresas, permissões, tudo. Limpar o
    // cache é o que garante que nenhuma consulta da organização anterior
    // reapareça (CERES-008 §15).
    queryClient.clear();
  }, []);

  const entrarNaPlataforma = useCallback(async (): Promise<void> => {
    const body = await api.post<{ user: CurrentUser; legal?: SituacaoLegalDaSessao }>(
      '/auth/contexto/plataforma',
    );
    geracao.current += 1;
    setUser(body.user);
    aplicarIdiomaEFuso(body.user);
    setLegal(body.legal ?? LEGAL_DESCONHECIDO);
    queryClient.clear();
  }, []);

  const voltarAEscolha = useCallback(async (): Promise<void> => {
    const body = await api.post<{ user: CurrentUser; legal?: SituacaoLegalDaSessao }>(
      '/auth/contexto/escolha',
    );
    geracao.current += 1;
    setUser(body.user);
    aplicarIdiomaEFuso(body.user);
    setLegal(body.legal ?? LEGAL_DESCONHECIDO);
    queryClient.clear();
  }, []);

  const permissions = useMemo(() => new Set(user?.permissions ?? []), [user]);

  /**
   * AS CAPACIDADES CONTRATADAS DESTA SESSÃO (CERES-018A).
   *
   * Sem sessão resolvida, o conjunto é VAZIO — e vazio nega. A versão anterior
   * devolvia `null` enquanto a lista não tinha chegado, e `hasFeature` então
   * respondia `true` para qualquer chave: "não sei quais são as capacidades,
   * então libero". Existia por compatibilidade de rollout entre API e web, e o
   * preço era desproporcional ao benefício — bastava a lista não chegar (API
   * antiga, resposta malformada, campo renomeado) para o produto inteiro
   * aparecer destravado na tela de quem não contratou.
   *
   * A regra passa a ser a única defensável:
   *
   *   enquanto não sabemos, não assumimos.
   *
   * Nada pisca por causa disso: o roteador segura a aplicação num carregador
   * enquanto `loading` é verdadeiro, então nenhuma tela protegida chega a ser
   * montada antes de a lista existir.
   *
   * E isto continua sendo EXPERIÊNCIA, não autorização: o backend afirma o
   * mesmo gate em `assertProductFeature`, inclusive para quem não entra por
   * HTTP. Esconder o botão nunca foi a tranca — é a cortesia de não oferecer o
   * que a organização não pode usar.
   */
  const productFeatures = useMemo(() => new Set(user?.productFeatures ?? []), [user]);

  const hasFeature = useCallback(
    (feature: string): boolean => productFeatures.has(feature),
    [productFeatures],
  );

  const sessaoPronta = sessaoEstaPronta(loading, user);

  const can = useCallback(
    (permission: string): boolean => permissions.has(permission),
    [permissions],
  );
  const canAny = useCallback(
    (list: string[]): boolean => list.some((permission) => permissions.has(permission)),
    [permissions],
  );

  const value = useMemo<SessionState>(
    () => ({
      user,
      legal,
      loading,
      login,
      logout,
      refresh,
      trocarOrganizacao,
      entrarNaPlataforma,
      voltarAEscolha,
      can,
      canAny,
      hasFeature,
      sessaoPronta,
    }),
    [
      user,
      legal,
      loading,
      login,
      logout,
      refresh,
      trocarOrganizacao,
      entrarNaPlataforma,
      voltarAEscolha,
      can,
      canAny,
      hasFeature,
      sessaoPronta,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error('useSession precisa estar dentro de SessionProvider.');
  }
  return context;
}

/** Usuário autenticado garantido — para telas dentro da área protegida. */
export function useCurrentUser(): CurrentUser {
  const { user } = useSession();
  if (user === null) {
    throw new Error('Nenhum usuário autenticado no contexto.');
  }
  return user;
}
